import { LocationDescriptorObject, parsePath } from 'history';
import { isFeatureEnabled } from './EnvironmentConfig';

/** Where a content path should point, plus the parameters needed to build it. */
export type ContentPathOptions =
  | {
      readonly contentId: string;
      readonly mode: 'draft';
      readonly role: 'author' | 'admin' | 'bar-raiser' | 'read-only';
    }
  | { readonly contentId: string; readonly mode: 'publication'; readonly workflowId?: number };

/** Returns the content path, routing to the v2 viewer when UxRedesign is enabled. */
export function getContentPath(options: ContentPathOptions): string {
  // When UxRedesign is on, route to the single v2 /view page; it auto-detects the user's role.
  if (isFeatureEnabled('UxRedesign')) {
    if (options.mode === 'draft') {
      return `/view/${options.contentId}?mode=draft`;
    }

    const workflowParam =
      options.workflowId !== undefined ? `&workflowId=${options.workflowId}` : '';
    return `/view/${options.contentId}?mode=publication${workflowParam}`;
  }

  // Legacy publication view, optionally pinned to a workflow version.
  if (options.mode === 'publication') {
    return options.workflowId !== undefined
      ? `/content/${options.contentId}/${options.workflowId}`
      : `/content/${options.contentId}`;
  }

  // Legacy draft view — the read-only variant has no role segment.
  return options.role === 'read-only'
    ? `/content-viewer/${options.contentId}`
    : `/content-viewer/${options.role}/${options.contentId}`;
}

/**
 * Returns the content location for an object-form `<Link to>` or `history.push`, with any query
 * string split into `search`. Use it instead of passing {@link getContentPath} as a `pathname`:
 * history keeps an object `pathname` verbatim, so a `?mode=…` query would leak into the
 * `:contentId` route param.
 */
export function getContentLocation<S>(
  options: ContentPathOptions,
  state?: S,
): LocationDescriptorObject<S> {
  return { ...parsePath(getContentPath(options)), state };
}
